import type { ClienteApi } from '../cliente';
import { claveIdempotencia, consulta, type Pagina } from '../comun';
import type { components as Reservas } from '../tipos/reservas-sala';
import type { components as Web } from '../tipos/web';
import { operacionesWebPublicaCliente } from './webPublicaCliente';

/**
 * Operaciones de las páginas del cliente de Reservas, con su `operationId` y los tipos generados de `reservas-sala.yaml` y
 * `web.yaml`. Van con el cliente de la API de la zona del cliente (`cliente/apiCliente.ts`), nunca con la sesión del equipo.
 *
 * - Reserva pública (8.11) y estancia (8.9, cliente): con la **clave pública del negocio** (`X-Api-Key`): la de su web
 *   (`getPublicSite`, `api_key`) o la del widget (`getPublicWidget`, la clave con la que se abre). Crear la reserva lleva
 *   además el token de cliente (tras identificarse, 2.9), el antibot (`X-Captcha-Token`, G76) e `Idempotency-Key`.
 * - «Mis reservas» (8.12): con el token de cliente, por su perfil en cada negocio (`/customer-account/profiles/{id}`).
 */
type Esquema<N extends keyof Reservas['schemas']> = Reservas['schemas'][N];

export type Catalogo = Esquema<'ReservationCatalog'>;
export type ZonaCatalogo = Catalogo['zones'][number];
export type OfertaCatalogo = Catalogo['event_offers'][number];
export type BusquedaPublica = Esquema<'ReservationSearch'>;
export type HuecosPublicos = Esquema<'ReservationSearchResult'>;
export type PeticionPublica = Esquema<'ReservationRequest'>;
export type PresupuestoPublico = Esquema<'ReservationQuote'>;
export type ReservaEnLinea = Esquema<'OnlineReservationCreate'>;
export type ReservaEnLineaCreada = Esquema<'ReservationCreated'>;
export type MiReserva = Esquema<'MyReservation'>;
export type ListaMisReservas = Esquema<'MyReservationList'>;
export type CambioMiReserva = Esquema<'MyReservationChange'>;
export type MiReservaCambiada = Esquema<'MyReservationCreated'>;
export type CondicionesMiCancelacion = Esquema<'CancellationTerms'>;
export type WebPublica = Web['schemas']['PublicSite'];
export type WidgetPublico = Web['schemas']['PublicWidget'];

const negocioPublico = (businessId: string, resto: string): `/v1/${string}` => `/v1/businesses/${encodeURIComponent(businessId)}${resto}`;
const perfil = (id: string): `/v1/${string}` => `/v1/customer-account/profiles/${encodeURIComponent(id)}`;
const llave = (clave: string): Record<string, string> => ({ 'X-Api-Key': clave });

export function operacionesMisReservas(api: ClienteApi) {
    return {
        // ---- Marca del negocio (G614, G618) ----
        ...operacionesWebPublicaCliente(api),

        // ---- 8.11 Reserva pública (el mismo motor que el panel, G38) ----
        /** getReservationCatalog: zonas públicas con su `unit` (G658), `booking_window` y ofertas de eventos (con `event_id`, solo las de ese evento, G905). */
        getReservationCatalog: (businessId: string, clave: string, eventId?: string) =>
            api.pedir<Catalogo>('GET', negocioPublico(businessId, `/reservation-catalog${consulta({ event_id: eventId })}`), { sinSesion: true, cabeceras: llave(clave) }),
        /** searchReservationSlots con la clave pública del negocio (con `event_offer_id`, los huecos de esa oferta de un evento, G905) (la del panel, con la sesión, está en `ops/reservasGestion.ts`). */
        searchReservationSlotsPublic: (businessId: string, clave: string, datos: BusquedaPublica) =>
            api.pedir<HuecosPublicos>('POST', negocioPublico(businessId, '/reservation-searches'), { cuerpo: datos, sinSesion: true, cabeceras: llave(clave) }),
        /** quoteReservation con la clave pública del negocio. */
        quoteReservationPublic: (businessId: string, clave: string, datos: PeticionPublica) =>
            api.pedir<PresupuestoPublico>('POST', negocioPublico(businessId, '/reservation-quotes'), { cuerpo: datos, sinSesion: true, cabeceras: llave(clave) }),
        /**
         * createOnlineReservation: con el token de cliente, el antibot y `expected_total` (si cambió, `409 conflict`); un hueco
         * que se ha ocupado, `409 unavailable`. Con pago, `next_action.payment_url` (30 min, G39).
         */
        createOnlineReservation: (businessId: string, clave: string, datos: ReservaEnLinea, captcha: string | null, clave2 = claveIdempotencia()) =>
            api.pedir<ReservaEnLineaCreada>('POST', negocioPublico(businessId, '/online-reservations'), {
                cuerpo: datos,
                idempotencia: clave2,
                cabeceras: { ...llave(clave), ...(captcha === null ? {} : { 'X-Captcha-Token': captcha }) },
            }),

        // ---- 8.12 «Mis reservas» (token de cliente) ----
        listMyReservations: (perfilId: string, cuando: 'upcoming' | 'past' = 'upcoming', pagina: Pagina = {}) =>
            api.pedir<ListaMisReservas>('GET', `${perfil(perfilId)}/reservations${consulta({ when: cuando, ...pagina })}`),
        getMyReservation: (perfilId: string, reservaId: string) => api.pedir<MiReserva>('GET', `${perfil(perfilId)}/reservations/${encodeURIComponent(reservaId)}`),
        /** changeMyReservation: solo fecha, hora o personas (G19), con `allowed_actions` que lo permita. */
        changeMyReservation: (perfilId: string, reserva: Pick<MiReserva, 'id' | 'version'>, datos: CambioMiReserva, clave = claveIdempotencia()) =>
            api.pedir<MiReservaCambiada>('POST', `${perfil(perfilId)}/reservations/${encodeURIComponent(reserva.id)}/changes`, {
                cuerpo: datos,
                idempotencia: clave,
                siCoincide: `"${reserva.id}.${String(reserva.version)}"`,
            }),
        getMyReservationCancellationTerms: (perfilId: string, reservaId: string) =>
            api.pedir<CondicionesMiCancelacion>('GET', `${perfil(perfilId)}/reservations/${encodeURIComponent(reservaId)}/cancellation-terms`),
        /** cancelMyReservation: también con el negocio en solo lectura (G55); si lo que vio cambió, `409`. */
        cancelMyReservation: (perfilId: string, reservaId: string, datos: Esquema<'MyReservationCancellation'>, clave = claveIdempotencia()) =>
            api.pedir<MiReserva>('POST', `${perfil(perfilId)}/reservations/${encodeURIComponent(reservaId)}/cancellation`, { cuerpo: datos, idempotencia: clave }),
    };
}

export type OperacionesMisReservas = ReturnType<typeof operacionesMisReservas>;
